import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Mail, Lock, AlertCircle, Terminal, ArrowRight, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

export default function LoginPage() {
  useSEO('/login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const { login, completeTwoFactor } = useAuth();
  const navigate = useNavigate();

  const done = () => {
    toast.success('Connexion réussie !');
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twoFactorRequired) {
        setChallenge(res.challenge);
        setPassword('');
      } else {
        done();
      }
    } catch (err) {
      setError(err.message || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeTwoFactor(challenge, code.trim());
      done();
    } catch (err) {
      const msg = err.message || 'Code incorrect';
      if (/expir|invalide/i.test(msg) && !/^Code/.test(msg)) {
        setChallenge(null);
        setCode('');
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">

      {/* ── GAUCHE — Formulaire ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-8">

          {/* Logo */}
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <ShieldCheck className="w-10 h-10 text-blue-500" />
              <span className="font-mono text-2xl font-black text-white uppercase tracking-tighter">CyberGuard</span>
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 mb-4">
              <Terminal className="w-3 h-3 text-blue-400" />
              <span className="text-blue-400 text-[10px] font-mono uppercase tracking-widest">Secure_Login_Terminal</span>
            </div>
            <h1 className="text-2xl font-black text-white font-mono uppercase tracking-tighter">Connexion</h1>
            <p className="text-zinc-500 text-xs font-mono mt-1 uppercase tracking-widest">Accédez à votre tableau de bord</p>
          </div>

          {/* Formulaire */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500/0 via-blue-500 to-blue-500/0 opacity-50" />

            {challenge ? (
            <form onSubmit={handleCode} className="space-y-5" data-testid="two-factor-form">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border-l-2 border-red-500 text-red-400 text-xs font-mono">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="login-code" className="text-xs font-bold text-blue-400 font-mono tracking-widest uppercase">
                  {useRecovery ? 'Code de secours' : 'Code de vérification'}
                </label>
                <p className="text-zinc-400 text-sm">
                  {useRecovery
                    ? 'Saisissez un de vos codes de secours (chacun ne sert qu’une fois).'
                    : 'Ouvrez votre application d’authentification et saisissez le code à 6 chiffres affiché pour CyberGuard.'}
                </p>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input
                    id="login-code"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode={useRecovery ? 'text' : 'numeric'}
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
                    className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white font-mono text-lg tracking-widest focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-4 font-black font-mono uppercase text-sm rounded-none transition-all flex items-center justify-center gap-2"
              >
                {loading ? <span className="animate-pulse">Vérification…</span> : <><ShieldCheck className="w-4 h-4" /> Valider <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="flex justify-between text-xs font-mono">
                <button type="button" onClick={() => { setUseRecovery(v => !v); setCode(''); setError(''); }} className="text-zinc-400 hover:text-blue-400">
                  {useRecovery ? 'Utiliser l’application' : 'Utiliser un code de secours'}
                </button>
                <button type="button" onClick={() => { setChallenge(null); setCode(''); setError(''); }} className="text-zinc-500 hover:text-white">
                  ← Retour
                </button>
              </div>
            </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border-l-2 border-red-500 text-red-400 text-xs font-mono">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label htmlFor="login-email" className="text-xs font-bold text-blue-400 font-mono tracking-widest uppercase">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="vous@entreprise.com"
                    className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="login-password" className="text-xs font-bold text-blue-400 font-mono tracking-widest uppercase">Mot de passe</label>
                  {/* ✅ Lien mot de passe oublié */}
                  <Link to="/forgot-password" className="text-zinc-400 hover:text-blue-400 font-mono text-xs transition-colors">
                    Mot de passe oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-4 font-black font-mono uppercase text-sm rounded-none transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="animate-pulse">Connexion…</span>
                ) : (
                  <><ShieldCheck className="w-4 h-4" /> Se connecter <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
            )}
          </div>

          <p className="text-center text-zinc-500 text-xs font-mono">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 transition-colors uppercase">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>

      {/* ── DROITE — Features ── */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-zinc-900/30 border-l border-zinc-800 p-12">
        <div className="max-w-md space-y-8">
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-blue-400 uppercase tracking-widest">System_Status</div>
            <h2 className="text-3xl font-black text-white font-mono uppercase tracking-tighter leading-none">
              Sécurisez<br /><span className="text-blue-500">votre infrastructure</span>
            </h2>
          </div>

          <div className="space-y-3">
            {[
              { color: 'bg-blue-500', label: 'Analyse SSL, HTTPS et headers de sécurité' },
              { color: 'bg-green-500', label: 'Vérification email SPF, DKIM et DMARC' },
              { color: 'bg-yellow-500', label: 'Score de sécurité de A à G' },
              { color: 'bg-purple-500', label: 'Rapport IA et guide de correction (plan Pro)' },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                <div className={`w-2 h-2 rounded-full ${color} shrink-0`} />
                <span className="text-zinc-300 text-xs font-mono">{label}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-800">
            {[
              { val: '50+', label: 'Checks' },
              { val: '<2min', label: 'Scan' },
              { val: 'IA', label: 'Rapports' },
            ].map(({ val, label }) => (
              <div key={label} className="text-center">
                <div className="text-xl font-black font-mono text-blue-400">{val}</div>
                <div className="text-[9px] text-zinc-600 font-mono uppercase tracking-widest">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
